/**
 * Draws thrown eggs (entities/thrown.ts) as camera-facing item sprites, like
 * vanilla's ThrownItemRenderer, plus the egg-shell crumbs they burst into.
 */

import * as THREE from 'three';
import type { Thrown } from '../entities/thrown';

const SHELLS = 64;

export class ThrownRenderer {
  readonly group = new THREE.Group();
  private readonly mats = new Map<string, THREE.SpriteMaterial>();
  private readonly mat: THREE.SpriteMaterial;
  private readonly pool: THREE.Sprite[] = [];
  private readonly shells: { s: THREE.Sprite; life: number; vx: number; vy: number; vz: number }[] = [];
  private nextShell = 0;

  constructor() {
    this.mat = this.matFor('egg');
    const shellMat = new THREE.SpriteMaterial({ color: 0xe8dcc0 });
    for (let i = 0; i < SHELLS; i++) {
      const s = new THREE.Sprite(shellMat);
      s.scale.setScalar(0.08);
      s.visible = false;
      this.group.add(s);
      this.shells.push({ s, life: 0, vx: 0, vy: 0, vz: 0 });
    }
  }

  /** Material of an item sprite (`mc/item/<name>.png`): eggs offline, snowballs and pearls too in server mode. */
  private matFor(name: string): THREE.SpriteMaterial {
    let m = this.mats.get(name);
    if (!m) {
      const tex = new THREE.TextureLoader().load(`mc/item/${name}.png`);
      tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter; tex.colorSpace = THREE.SRGBColorSpace;
      m = new THREE.SpriteMaterial({ map: tex, transparent: true, alphaTest: 0.1 });
      this.mats.set(name, m);
    }
    return m;
  }

  /** Vanilla ItemParticle burst: 8 crumbs with a little random velocity. */
  shatter(x: number, y: number, z: number): void {
    for (let i = 0; i < 8; i++) {
      const p = this.shells[this.nextShell];
      this.nextShell = (this.nextShell + 1) % SHELLS;
      p.s.position.set(x, y, z);
      p.s.visible = true;
      p.life = 0.4 + Math.random() * 0.4;
      p.vx = (Math.random() - 0.5) * 3; p.vy = Math.random() * 2.5; p.vz = (Math.random() - 0.5) * 3;
    }
  }

  update(eggs: readonly (Thrown & { sprite?: string })[], alpha: number, dt: number): void {
    while (this.pool.length < eggs.length) {
      const s = new THREE.Sprite(this.mat);
      s.scale.setScalar(0.25);
      this.group.add(s);
      this.pool.push(s);
    }
    for (let i = 0; i < this.pool.length; i++) {
      const s = this.pool[i], e = eggs[i];
      s.visible = !!e;
      if (e) {
        s.material = e.sprite ? this.matFor(e.sprite) : this.mat;
        s.position.set(e.px + (e.x - e.px) * alpha, e.py + (e.y - e.py) * alpha + 0.125, e.pz + (e.z - e.pz) * alpha);
      }
    }
    for (const p of this.shells) {
      if (p.life <= 0) continue;
      p.life -= dt;
      if (p.life <= 0) { p.s.visible = false; continue; }
      p.vy -= 12 * dt;
      p.s.position.x += p.vx * dt; p.s.position.y += p.vy * dt; p.s.position.z += p.vz * dt;
    }
  }
}
